import React, { useEffect, useRef } from 'react'
import CSS from 'csstype'

import {
  useInspectorContext,
  useMapPRMarinaRectToCanvasRelativeRect,
} from '../../../hooks/inspector'
import {
  ElementType,
  SketchElement,
  isTopLevelFrameElement,
} from '../../../../../inspector'
import { ArtboardOverlay } from '../../../../../canvas/components'
import { OVERLAYS_Z_INDEX } from '../constants'

import { LayerOverlay as LayerOverlayStyledComponent } from '../FrameDetailInspectorOverlay.styles'
import * as S from './SelectedElementOverlays.styles'
import { Rect } from '@sketch-hq/sketch-web-renderer'
import { useStableHandler } from '@sketch/utils'

import {
  useArtboardEvents,
  useHandleArtboardsInteractions,
} from '../../../../../canvas/hooks'

// The corner SVGs are drawn using an outer border which means they are drawn at a 1
// pixel offset.
const CORNER_OFFSET = 1
const SELECTED_LAYER_MIN_SIZE_TO_SHOW_CORNERS = 12

type SelectedElementOverlaysProps = {
  selectedElement: SketchElement | null
  showFrameOverlay: boolean
}
export function SelectedElementOverlays({
  selectedElement,
  showFrameOverlay,
}: SelectedElementOverlaysProps) {
  const { sketchSceneRootElement } = useInspectorContext()

  // We don't show artboard as selected element.
  if (!selectedElement || !sketchSceneRootElement) {
    return null
  }

  return (
    <SelectionRectangle
      selectedElement={selectedElement}
      showFrameOverlay={showFrameOverlay}
    />
  )
}

type SelectionRectangleProps = {
  selectedElement: SketchElement
  showFrameOverlay: boolean
}
function SelectionRectangle({
  selectedElement,
  showFrameOverlay,
}: SelectionRectangleProps) {
  const bounds = selectedElement.prMarinaNode.getAbsoluteLayerBounds()
  const canvasRelativeRect = useMapPRMarinaRectToCanvasRelativeRect(bounds)

  if (!canvasRelativeRect) {
    return null
  }

  const { x, y, width, height } = canvasRelativeRect
  // Don't show the corners if the selected rectangle is very small
  // as the corner won't fit.
  const showMinimalVersion =
    width < SELECTED_LAYER_MIN_SIZE_TO_SHOW_CORNERS ||
    height < SELECTED_LAYER_MIN_SIZE_TO_SHOW_CORNERS

  const artboardOverlay = showFrameOverlay &&
    isTopLevelFrameElement(selectedElement) && (
      <ArtboardSelectionRectangle
        artboardName={selectedElement.name}
        artboardUUID={selectedElement.elementUUID}
        bounds={canvasRelativeRect}
        isSymbol={selectedElement.type === ElementType.SymbolMaster}
      />
    )

  if (showMinimalVersion) {
    return (
      <>
        {artboardOverlay}
        <LayerOverlayStyledComponent
          $borderSize={1}
          $color="orange"
          $width={width}
          $height={height}
          $x={x}
          $y={y}
          $zIndex={OVERLAYS_Z_INDEX.SELECTION_RECTANGLE}
        />
      </>
    )
  }

  const cornerStyles: CSS.Properties = {
    position: 'absolute',
    width: `6px`,
    height: `6px`,
  }

  return (
    <>
      {artboardOverlay}
      <S.SelectionRectangleContainer
        $width={width}
        $height={height}
        $x={x}
        $y={y}
      >
        <S.SelectionRectangleTopEdgeLine />
        <S.SelectionRectangleBottomEdgeLine />
        <S.SelectionRectangleLeftEdgeLine />
        <S.SelectionRectangleRightEdgeLine />

        <S.TopLeftCornerIcon
          style={{
            top: -CORNER_OFFSET,
            left: -CORNER_OFFSET,
            ...cornerStyles,
          }}
        />
        <S.TopRightCornerIcon
          style={{
            top: -CORNER_OFFSET,
            right: -CORNER_OFFSET,
            ...cornerStyles,
          }}
        />
        <S.BottomLeftCornerIcon
          style={{
            bottom: -CORNER_OFFSET,
            left: -CORNER_OFFSET,
            ...cornerStyles,
          }}
        />
        <S.BottomRightCornerIcon
          style={{
            bottom: -CORNER_OFFSET,
            right: -CORNER_OFFSET,
            ...cornerStyles,
          }}
        />
      </S.SelectionRectangleContainer>
    </>
  )
}

interface ArtboardSelectionRectangleProps {
  artboardUUID: string
  artboardName: string
  bounds: Rect
  isSymbol?: boolean
}

function ArtboardSelectionRectangle(props: ArtboardSelectionRectangleProps) {
  const { artboardUUID, artboardName, bounds, isSymbol } = props
  const artboardRef = useRef<HTMLDivElement>(null)

  const { onGoToArtboard } = useHandleArtboardsInteractions()
  const { setSelectedElement } = useInspectorContext()

  const artboardEvents = useArtboardEvents((event, eventType) => {
    if (eventType === 'cta-click') {
      onGoToArtboard(artboardUUID, 'CTA')
    }
  })

  const handleWindowKeyPress = useStableHandler((event: KeyboardEvent) => {
    if (
      event.key === 'Enter' &&
      global.document.activeElement === artboardRef.current
    ) {
      onGoToArtboard?.(artboardUUID!, 'keyPressEnter')
    } else if (event.key === 'Escape') {
      setSelectedElement(null)
    }
  })

  useEffect(() => {
    window.document.body.addEventListener('keydown', handleWindowKeyPress)

    return () => {
      window.document.body.removeEventListener('keydown', handleWindowKeyPress)
    }
  }, [handleWindowKeyPress])

  useEffect(() => {
    /**
     * Force focus on the element
     * given it was just mounted and not a the same as the selected state
     */
    artboardRef.current?.focus()
  }, [])

  return (
    <ArtboardOverlay
      artboardName={artboardName}
      artboardUUID={artboardUUID}
      bounds={bounds}
      events={artboardEvents}
      ref={artboardRef}
      isSymbol={isSymbol}
      selected
    />
  )
}
